import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SpiritualConfetti from '../SpiritualConfetti';
import { clearCart } from '../../utils/cartUtils';
import { trackPurchase } from '../../utils/analytics';
import './PaymentSuccessPage.css';

interface PaymentSuccessState {
  orderId: string;
  amount: number | string;
  items?: any[];
  billingInfo: any;
  paymentMethod?: string;
  paymentStatus?: string;
  paymentDueDate?: string;
  isBookingPayment?: boolean;
  bookingId?: string;
  serviceName?: string;
  selectedDate?: string;
  selectedTime?: string;
  selectedAddress?: any;
  paymentMode?: 'PREPAID' | 'POSTPAID';
  planName?: string;
  duration?: string;
  serviceImage?: string;
}

const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as PaymentSuccessState;
  const [showConfetti, setShowConfetti] = useState(false);

  // Fire GA4 purchase event once per successful order
  useEffect(() => {
    if (!state?.orderId) return;

    const numericAmt =
      typeof state.amount === 'string'
        ? parseFloat(state.amount.replace(/[^0-9.-]+/g, ''))
        : state.amount ?? 0;

    // Build GA4 items array
    const items = state.items && state.items.length > 0
      ? state.items.map((item: any, idx: number) => ({
          item_id: item.id || `item_${idx}`,
          item_name: item.name || item.serviceName || 'Service',
          item_category: item.category || 'Puja Service',
          item_variant: item.planName || item.variant,
          price: typeof item.price === 'string'
            ? parseFloat(item.price.replace(/[^0-9.-]+/g, ''))
            : item.price ?? 0,
          quantity: item.quantity ?? 1,
          currency: 'INR',
        }))
      : [{
          item_id: state.bookingId || state.orderId,
          item_name: state.serviceName || 'Puja Service',
          item_category: 'Puja Service',
          item_variant: state.planName,
          price: numericAmt,
          quantity: 1,
          currency: 'INR',
        }];

    const isPayLater =
      state.paymentMode === 'POSTPAID' || state.paymentMethod === 'paylater';

    trackPurchase({
      transactionId: state.orderId,
      value: numericAmt,
      items,
      paymentMethod: isPayLater ? 'pay_later' : state.paymentMethod || 'razorpay',
      tax: parseFloat((numericAmt - numericAmt / 1.18).toFixed(2)), // 18% GST back-calculation
    });
  }, [state?.orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save booking to order history and clear cart - must be before early return
  useEffect(() => {
    if (state && state.orderId) {
      // Save booking to history if it's a booking payment
      if (state.isBookingPayment && state.serviceName) {
        saveBookingToHistory();
      }
      
      // Clear cart after any successful payment/booking
      clearCart();
      console.log('✅ Cart cleared after successful payment/booking');
    }
  }, [state]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Trigger confetti animation on component mount
  useEffect(() => {
    if (state) {
      // Small delay to ensure page is rendered before confetti starts
      const timer = setTimeout(() => {
        setShowConfetti(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [state]);

  // Determine confetti settings based on payment type
  const getConfettiSettings = () => {
    const isPayLater = paymentMode === 'POSTPAID' || paymentMethod === 'paylater';
    const isServiceBooking = isBookingPayment && serviceName;
    
    if (isServiceBooking) {
      return {
        duration: isPayLater ? 4000 : 6000, // Longer celebration for paid bookings
        pieceCount: isPayLater ? 50 : 80    // More confetti for completed payments
      };
    }
    
    return {
      duration: 5000,
      pieceCount: 60
    };
  };

  const saveBookingToHistory = () => {
    if (!state) return;
    
    try {
      const isPayLater = state.paymentMode === 'POSTPAID' || state.paymentMethod === 'paylater';
      
      const bookingOrder = {
        id: state.bookingId || `booking_${Date.now()}`, // Use serviceRequestUuid from API response
        orderId: state.orderId, // Use proper formatted order ID from API response
        serviceName: state.serviceName!,
        planName: state.planName || undefined,
        bookingDate: new Date().toISOString(),
        serviceDate: state.selectedDate || '',
        serviceTime: state.selectedTime || '',
        duration: state.duration || undefined,
        status: isPayLater ? 'confirmed' : 'confirmed',
        paymentStatus: isPayLater ? 'pending' : 'paid',
        paymentMode: state.paymentMode || (isPayLater ? 'POSTPAID' : 'PREPAID'),
        totalAmount: state.amount,
        address: {
          name: state.selectedAddress?.name || state.selectedAddress?.label,
          label: state.selectedAddress?.label || state.selectedAddress?.name,
          line1: state.selectedAddress?.addressLine1 || state.selectedAddress?.line1 || '',
          line2: state.selectedAddress?.addressLine2 || state.selectedAddress?.line2,
          city: state.selectedAddress?.city || '',
          state: state.selectedAddress?.state || '',
          postalCode: state.selectedAddress?.pincode || state.selectedAddress?.postalCode || '',
          type: state.selectedAddress?.type || state.selectedAddress?.addressType || 'home'
        },
        serviceImage: state.serviceImage || '/images/carousel/default-service.jpg',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        paymentDueDate: state.paymentDueDate || undefined
      };

      // Get existing orders from localStorage
      const existingOrders = localStorage.getItem('booking_history');
      const orders = existingOrders ? JSON.parse(existingOrders) : [];
      
      // Add new order to the beginning of the array
      orders.unshift(bookingOrder);
      
      // Keep only the last 50 orders to prevent localStorage bloat
      const trimmedOrders = orders.slice(0, 50);
      
      // Save back to localStorage
      localStorage.setItem('booking_history', JSON.stringify(trimmedOrders));
      
      console.log('✅ Booking saved to order history:', bookingOrder);
    } catch (error) {
      console.error('❌ Error saving booking to history:', error);
    }
  };

  if (!state) {
    navigate('/');
    return null;
  }

  const { 
    orderId, 
    amount, 
    items = [], 
    billingInfo, 
    paymentMethod, 
    paymentStatus, 
    paymentDueDate,
    isBookingPayment = false,
    bookingId,
    serviceName,
    selectedDate,
    selectedTime,
    paymentMode
  } = state;
  
  // Convert amount to number for consistent handling
  const numericAmount = typeof amount === 'string' 
    ? parseFloat(amount.replace(/[^0-9.-]+/g, "")) 
    : amount;
  
  // Determine if this is pay later based on payment mode or method
  const isPayLater = paymentMode === 'POSTPAID' || paymentMethod === 'paylater';
  const isServiceBooking = isBookingPayment && serviceName;
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      
      // Check if it's a valid date
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if invalid
      }
      
      // Format with Indian timezone
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString || '';
    }
  };

  const confettiSettings = getConfettiSettings();

  return (
    <div className="payment-success-page">
      {/* Spiritual Confetti Animation */}
      <SpiritualConfetti 
        isActive={showConfetti} 
        duration={confettiSettings.duration} 
        pieceCount={confettiSettings.pieceCount} 
      />
      
      <div className="success-container">
        <div className="success-card">
          <div className={`success-icon ${isPayLater ? 'booking-confirmed' : ''}`}>
            {isPayLater ? (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#ff6b35"/>
                <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 6v6l4 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#28a745"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          
          <h1>
            {isServiceBooking 
              ? (isPayLater ? 'Booking Successful!' : 'Payment Successful!')
              : (isPayLater ? 'Booking Confirmed!' : 'Payment Successful!')
            }
          </h1>
          <p className="success-message">
            {isPayLater 
              ? (isServiceBooking 
                  ? `Your ${serviceName} service has been booked successfully! Complete payment before the due date.`
                  : 'Your booking has been confirmed! Complete payment before the due date .')
              : (isServiceBooking
                  ? `Payment completed! Your ${serviceName} service is confirmed and scheduled.`
                  : 'Thank you for your payment! Your order has been confirmed.')
            }
          </p>          {isPayLater && (
            <div className="pay-later-notice">
              <div className="notice-icon">⏰</div>
              <div className="notice-content">
                <h3>Payment Required Before Service</h3>
                <p>Please complete your payment by <strong>{formatDate(paymentDueDate)}</strong></p>
                <p className="notice-sub">If payment is not received by this date, your booking may be cancelled. We will remind for payment before service cancellation</p>
              </div>
            </div>
          )}

          <div className="order-details">
            <div className="detail-row">
              <span className="label">Order ID:</span>
              <span className="value">{orderId}</span>
            </div>
            <div className="detail-row">
              <span className="label">{isPayLater ? 'Total Amount:' : 'Amount Paid:'}</span>
              <span className="value amount">₹{numericAmount.toFixed(2)}</span>
            </div>
            <div className="detail-row">
              <span className="label">Payment Method:</span>
              <span className="value">{isPayLater ? 'Pay Later' : 'Online Payment'}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className={`value status ${isPayLater ? 'pending' : 'confirmed'}`}>
                {isPayLater ? 'Booking Confirmed - Payment Pending' : 'Confirmed & Paid'}
              </span>
            </div>
            {isPayLater && (
              <div className="detail-row">
                <span className="label">Payment Due:</span>
                <span className="value due-date">{formatDate(paymentDueDate)}</span>
              </div>
            )}
          </div>

          <div className="next-steps">
            <h3>What's Next?</h3>
            <ul>
              <li>You will receive a confirmation email shortly</li>
              <li>Our team will contact you within 24 hours</li>
              <li>We'll schedule the puja services as per your preference</li>
              {isPayLater && (
                <>
                  <li><strong>Complete payment within 72 hours of booking</strong></li>
                  <li>Payment reminder will be sent if payment is not received by the due date</li>
                </>
              )}
            </ul>
          </div>

          <div className="action-buttons">
            <button onClick={() => navigate('/')} className="home-btn">
              Continue Exploring
            </button>
            {(isServiceBooking || (isBookingPayment && items && items.length > 0)) && (
              <button onClick={() => navigate('/orders')} className="orders-btn">
                View My Orders
              </button>
            )}
            {isPayLater && (
              <button onClick={() => navigate('/profile')} className="manage-booking-btn">
                Manage Booking
              </button>
            )}
            {/* <button onClick={() => window.print()} className="print-btn">
              Print {isPayLater ? 'Booking Confirmation' : 'Receipt'}
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;